import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { SiFacebook, SiLinkedin, SiInstagram } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';
import { ROUTE_PATHS, SECTION_IDS } from '@/lib/index';
import { springPresets } from '@/lib/motion';
import { IMAGES } from '@/assets/images';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const navigationItems = [
    { label: 'Sobre', href: `#${SECTION_IDS.SOBRE}` },
    { label: 'Modelo Operacional', href: `#${SECTION_IDS.MODELO_OPERACIONAL}` },
    { label: 'Sustentabilidade', href: `#${SECTION_IDS.SUSTENTABILIDADE}` },
    { label: 'Integração Territorial', href: `#${SECTION_IDS.INTEGRACAO_TERRITORIAL}` },
    { label: 'Compromisso', href: `#${SECTION_IDS.COMPROMISSO}` },
    { label: 'Contacto', href: `#${SECTION_IDS.CONTACTO}` },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      const offset = headerHeight;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center space-x-3 group">
              <div className="relative">
                <img 
                  src={IMAGES.LOGO_SOJATERRA} 
                  alt="SojaTerra Logo" 
                  className="h-12 w-auto transition-transform duration-200 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-foreground text-lg leading-tight">SojaTerra</span>
                <span className="text-xs text-muted-foreground leading-tight">Kussopa Comércio Geral</span>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted"
                >
                  {item.label}
                </a>
              ))}
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden border-t border-border bg-background"
            >
              <nav className="container mx-auto px-4 py-4 flex flex-col space-y-2">
                {navigationItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }} className="flex-1">
        {children}
      </main>

      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="relative">
                  <img 
                    src={IMAGES.LOGO_SOJATERRA} 
                    alt="SojaTerra Logo" 
                    className="h-10 w-auto"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="font-semibold text-foreground text-lg leading-tight">SojaTerra</span>
                  <span className="text-xs text-muted-foreground leading-tight">Kussopa Comércio Geral</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Plataforma agrícola no Planalto Central de Angola, comprometida com produção sustentável e desenvolvimento regional.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-4">Navegação</h3>
              <nav className="flex flex-col space-y-2">
                {navigationItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-4">Contacto</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Kussopa Comércio Geral</p>
                <p>Bié, Angola</p>
                <p>Email: info@sojaterra.ao</p>
                <p>Tel: +244 XXX XXX XXX</p>
              </div>
              <div className="flex items-center space-x-4 mt-4">
                <a
                  href="#"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Facebook"
                >
                  <SiFacebook size={20} />
                </a>
                <a
                  href="#"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="LinkedIn"
                >
                  <SiLinkedin size={20} />
                </a>
                <a
                  href="#"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Instagram"
                >
                  <SiInstagram size={20} />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>© 2026 SojaTerra - Kussopa Comércio Geral. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
